'use client'

import * as React from 'react'
import { RotateCcw } from 'lucide-react'
import { useInView, useReducedMotion } from 'framer-motion'

// Shared chrome for the explanatory motion graphics. Each explainer is a
// short, one-shot sequence that shows a product mechanism (collect →
// rights → tag → display → measure, a pin becoming a cart line, a review
// becoming a Google rich result). The frame owns the three things every
// explainer needs and none should re-implement: play once when scrolled
// into view, resolve instantly under reduced motion, and offer a replay.

export type ExplainerPlayback = {
  /** True once the sequence should run (in view, or replayed). */
  play: boolean
  /** True when the viewer prefers reduced motion; render the final state. */
  reduce: boolean
  /** Delay helper: returns 0 under reduced motion, otherwise the given seconds. */
  d: (seconds: number) => number
}

export function ExplainerFrame({
  eyebrow,
  title,
  caption,
  children,
  className = '',
}: {
  eyebrow: string
  title: string
  caption?: string
  children: (p: ExplainerPlayback) => React.ReactNode
  className?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion() ?? false
  const [run, setRun] = React.useState(0)
  const play = inView || run > 0
  const d = React.useCallback((s: number) => (reduce ? 0 : s), [reduce])

  return (
    <figure ref={ref} className={`relative ${className}`}>
      <div className="overflow-hidden rounded-3xl border bg-card shadow-xl shadow-primary/5">
        <div className="flex items-center justify-between gap-3 border-b bg-secondary/40 px-5 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{eyebrow}</p>
            <p className="truncate text-sm font-medium text-foreground">{title}</p>
          </div>
          {!reduce && (
            <button
              type="button"
              onClick={() => setRun((n) => n + 1)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Replay the animation"
            >
              <RotateCcw className="h-3 w-3" aria-hidden /> Replay
            </button>
          )}
        </div>
        <div className="p-5 sm:p-6" key={run}>
          {children({ play, reduce, d })}
        </div>
      </div>
      {caption && (
        <figcaption className="mt-3 text-sm text-muted-foreground text-pretty">{caption}</figcaption>
      )}
    </figure>
  )
}
